import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Homeowners from "@/pages/Homeowners";
import SettingsContent from "@/pages/SettingsContent";
import SettingsPricing from "@/pages/SettingsPricing";
import SettingsChecklists from "@/pages/SettingsChecklists";
import SettingsMaterials from "@/pages/SettingsMaterials";
import SettingsContracts from "@/pages/SettingsContracts";
import SignAgreement from "@/pages/SignAgreement";
import AckRates from "@/pages/AckRates";
import Inventory from "@/pages/Inventory";
import ReportsOwed from "@/pages/ReportsOwed";
import SettingsImages from "@/pages/SettingsImages";
import Account from "@/pages/Account";
import InstallCalendar from "@/pages/InstallCalendar";
import WorkOrders from "@/pages/WorkOrders";
import MyJobs from "@/pages/MyJobs";
import ConfirmInstall from "@/pages/ConfirmInstall";
import Users from "@/pages/Users";
import QuoteBuilder from "@/pages/QuoteBuilder";
import TemplateEditor from "@/pages/TemplateEditor";
import PublicQuote from "@/pages/PublicQuote";

function Protected({ children, admin }: { children: React.ReactElement; admin?: boolean }) {
  const { user } = useAuth();
  if (user === null) return <div className="flex h-screen items-center justify-center text-sm text-gray-500">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  if (user.role === "installer" && !window.location.pathname.startsWith("/my-jobs") && !window.location.pathname.startsWith("/sign/") && !window.location.pathname.startsWith("/ack-rates/") && !window.location.pathname.startsWith("/account")) return <Navigate to="/my-jobs" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/q/:token" element={<PublicQuote />} />
            <Route path="/confirm/:token" element={<ConfirmInstall />} />
            <Route path="/" element={<Navigate to="/quotes" replace />} />
            <Route path="/quotes" element={<Protected><Dashboard /></Protected>} />
            <Route path="/homeowners" element={<Protected><Homeowners /></Protected>} />
            <Route path="/calendar" element={<Protected><InstallCalendar /></Protected>} />
            <Route path="/work-orders" element={<Protected><WorkOrders /></Protected>} />
            <Route path="/work-orders/:id" element={<Protected><WorkOrders /></Protected>} />
            <Route path="/my-jobs" element={<Protected><MyJobs /></Protected>} />
            <Route path="/my-jobs/:id" element={<Protected><MyJobs /></Protected>} />
            <Route path="/settings/content" element={<Protected><SettingsContent /></Protected>} />
            <Route path="/settings/pricing" element={<Protected><SettingsPricing /></Protected>} />
            <Route path="/settings/checklists" element={<Protected><SettingsChecklists /></Protected>} />
            <Route path="/settings/materials" element={<Protected><SettingsMaterials /></Protected>} />
            <Route path="/settings/contracts" element={<Protected admin><SettingsContracts /></Protected>} />
            <Route path="/materials-report" element={<Navigate to="/inventory?tab=report" replace />} />
            <Route path="/inventory" element={<Protected admin><Inventory /></Protected>} />
            <Route path="/reports/owed" element={<Protected admin><ReportsOwed /></Protected>} />
            <Route path="/settings/images" element={<Protected><SettingsImages /></Protected>} />
            <Route path="/account" element={<Protected><Account /></Protected>} />
            <Route path="/sign/:token" element={<Protected><SignAgreement /></Protected>} />
            <Route path="/ack-rates/:token" element={<Protected><AckRates /></Protected>} />
            <Route path="/library" element={<Navigate to="/settings/content" replace />} />
            <Route path="/library/templates/:id" element={<Protected admin><TemplateEditor /></Protected>} />
            <Route path="/users" element={<Protected admin><Users /></Protected>} />
            <Route path="/quotes/:id" element={<Protected><QuoteBuilder /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="bottom-right" />
      </AuthProvider>
    </div>
  );
}
